import React from 'react';
import styled, { keyframes, css } from 'styled-components';
import { colors } from '../styles/colors.ts';

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const fadeOut = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${colors.black}99;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  animation: ${props => props.isClosing ? css`${fadeOut} 0.3s ease-out` : css`${fadeIn} 0.3s ease-out`};
  ${props => props.isZoomed && `
    overflow: hidden;
    width: 100vw;
    height: 100vh;
  `}
`;

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: auto;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ModalImage = styled.img`
  max-width: 90vw;
  max-height: 90vh;
  object-fit: contain;
  animation: ${props => props.isClosing ? css`${fadeOut} 0.3s ease-out` : css`${fadeIn} 0.3s ease-out`};
  transition: all 0.3s ease;
  margin: 0 auto;
  
  @media (hover: hover) {
    cursor: default;
    &:hover {
      cursor: default;
    }
  }

  ${props => props.isZoomed ? `
    max-width: none;
    max-height: calc(100% - 10rem);
    width: ${props.zoomLevel}%;
    height: ${props.zoomLevel}%;
  ` : `
    width: auto;
    height: auto;
  `}
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  color: white;
  font-size: 2rem;
  cursor: pointer;
  padding: 0.5rem;
  z-index: 1001;
  animation: ${props => props.isClosing ? css`${fadeOut} 0.3s ease-out` : css`${fadeIn} 0.3s ease-out`};
  
  &:hover {
    opacity: 0.8;
  }
`;

const TapToZoom = styled.span`
    color: ${(colors.white)};
    font-size: 0.6rem;
    font-weight: 600;
    position: absolute;
    bottom: 2.5vh;
    ${props => props.isZoomed && `
        display: none;
    `}
`;

const Modal = ({ image, onClose, zoomLevel = 150 }) => {
  const [isClosing, setIsClosing] = React.useState(false);
  const [isZoomed, setIsZoomed] = React.useState(false);
  const [isTouchDevice] = React.useState('ontouchstart' in window);

const handleCloseRef = React.useRef();

React.useEffect(() => {
// Define handleClose inside useEffect to avoid dependency warnings
const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
    onClose();
    }, 300); // Match this with animation duration
};

// Store the handleClose function in a ref so it can be accessed outside the effect
handleCloseRef.current = handleClose;

const handleEscape = (e) => {
    if (e.key === 'Escape') {
    handleClose();
    }
};

window.addEventListener('keydown', handleEscape);
return () => window.removeEventListener('keydown', handleEscape);
}, [onClose]);

  const handleImageClick = (e) => {
    e.stopPropagation();
    if (isTouchDevice) {
      setIsZoomed(!isZoomed);
    }
  };

  return (
    <ModalOverlay 
    onClick={() => handleCloseRef.current()}
      isClosing={isClosing}
      isZoomed={isZoomed}
    >
      <ImageContainer>
        <ModalImage
          src={image.src}
          alt={image.alt}
          onClick={handleImageClick}
          isClosing={isClosing}
          isZoomed={isZoomed}
          zoomLevel={zoomLevel}
        />
      </ImageContainer>
      <CloseButton 
        onClick={() => handleCloseRef.current()}
        isClosing={isClosing}
      >
        ×
      </CloseButton>
      {isTouchDevice && 
        <TapToZoom isZoomed={isZoomed}>
            Tap to zoom
        </TapToZoom>
      }
    </ModalOverlay>
  );
};

export default Modal;